import {
  locationToServiceRegion,
  regionToLocation,
} from '../connect/region.mappers';

/**
 * Maps Trimble Connect service region codes to the equivalent Geovector service region codes.
 */
const connectRegionToGeovectorRegionMapping = new Map<string, string>([
  ['na', 'na'],
  ['ap', 'ap'],
  ['ap2', 'au'],
  ['eu', 'eu'],
  ['eu2', 'uk'],
]);

/**
 * Converts a Trimble Connect region value to the matching Geovector service region code.
 *
 * Connect location names (e.g. `northAmerica`) are mapped first. Connect URL
 * region codes (e.g. `us`, `ap-au`) are lowercased and translated via friendly
 * location names, then mapped to Geovector.
 * Returns an empty string when the value is not a known region, preventing
 * arbitrary input from being propagated to downstream services.
 *
 * @param region - Trimble Connect region value, such as `na`, `US`, `ap2`, or `ap-au`.
 * @returns The mapped Geovector service region code, or an empty string when unsupported.
 */
export const connectRegionToGeovectorRegion = (region: string): string => {
  const trimmed = region.trim();
  const fromLocationName = locationToServiceRegion(trimmed);
  const mappedFromLocation =
    connectRegionToGeovectorRegionMapping.get(fromLocationName);
  if (mappedFromLocation !== undefined) {
    return mappedFromLocation;
  }

  const sanitizedRegion = locationToServiceRegion(
    regionToLocation(trimmed.toLowerCase())
  );
  return connectRegionToGeovectorRegionMapping.get(sanitizedRegion) ?? '';
};

export type GeovectorClientEnvironment = 'dev' | 'qa' | 'stage' | 'prod';
export const GEOVECTOR_SERVICE_REGIONS = [
  'na',
  'eu',
  'ap',
  'au',
  'uk',
] as const;
export type GeovectorRegion = (typeof GEOVECTOR_SERVICE_REGIONS)[number];

/**
 * Geovector regions available in each client environment.
 */
export const GEOVECTOR_REGIONS_BY_ENVIRONMENT: Readonly<
  Record<GeovectorClientEnvironment, readonly GeovectorRegion[]>
> = {
  prod: ['na', 'eu', 'ap', 'au', 'uk'],
  stage: ['na', 'eu', 'ap'],
  qa: ['na'],
  dev: ['na'],
};

/**
 * Checks whether a string is a supported Geovector service region code.
 *
 * @param region - Region value to validate.
 * @returns `true` when the value is a supported Geovector region.
 */
export const isGeovectorServiceRegion = (
  region?: string
): region is GeovectorRegion =>
  GEOVECTOR_SERVICE_REGIONS.some(
    (geovectorRegion) => geovectorRegion === region
  );

/**
 * Checks whether a region is supported in a specific Geovector client environment.
 *
 * @param env - Geovector client environment.
 * @param region - Region value to validate.
 * @returns `true` when the region is valid for the environment.
 */
export const isValidGeovectorRegionAndEnvironment = (
  env: GeovectorClientEnvironment,
  region?: string
): region is GeovectorRegion =>
  isGeovectorServiceRegion(region) &&
  GEOVECTOR_REGIONS_BY_ENVIRONMENT[env].includes(region);

/**
 * Gets the Geovector regions available in a client environment.
 *
 * @param env - Geovector client environment.
 * @returns The list of valid regions for the environment.
 */
export const getValidGeovectorRegionsForEnvironment = (
  env: GeovectorClientEnvironment
): readonly GeovectorRegion[] => GEOVECTOR_REGIONS_BY_ENVIRONMENT[env];

/**
 * Throws when a Geovector region is missing or unsupported by the selected environment.
 *
 * @param env - Geovector client environment.
 * @param region - Geovector region to validate.
 */
export const assertValidGeovectorRegionAndEnvironment = (
  env: GeovectorClientEnvironment,
  region: GeovectorRegion
): void => {
  if (!env || !region) {
    throw new Error(
      `Environment and region must be provided. Received env: "${env}", region: "${region}"`
    );
  }

  if (!isValidGeovectorRegionAndEnvironment(env, region)) {
    throw new Error(
      `Invalid region "${region}" for environment "${env}". Valid regions: ${getValidGeovectorRegionsForEnvironment(
        env
      ).join(', ')}`
    );
  }
};
